import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import PromoBanner from './components/PromoBanner';
import { CookieNotice } from './components/CookieNotice';
import { SupportButton } from './components/SupportButton';
import { analyticsConfig } from './config/analyticsConfig';
import { HomePage } from './pages/HomePage';
import { SquaresPage } from './pages/SquaresPage';
import { CubesPage } from './pages/CubesPage';
import { FractionsPage } from './pages/FractionsPage';
import { AdvancedPage } from './pages/AdvancedPage';
import { AboutPage } from './pages/AboutUsPage';

export function App() {
  useEffect(() => {
    // Prevent space bar from scrolling the page
    const preventSpaceScroll = (e: KeyboardEvent) => {
      if (e.code === 'Space' && e.target instanceof HTMLElement) {
        const target = e.target as HTMLElement;
        // If the focused element is a form control, link, button, or has role/button-like semantics, allow space to act normally
        const interactive = target.closest('input, textarea, button, select, a, [role="button"], [role="link"], [contenteditable="true"]');
        if (interactive) return;
        // Also allow typing into contenteditable elements
        if (target.isContentEditable) return;

        e.preventDefault();
      }
    };

    window.addEventListener('keydown', preventSpaceScroll);
    return () => window.removeEventListener('keydown', preventSpaceScroll);
  }, []);

  useEffect(() => {
    try {
      const id = analyticsConfig.trackingId;
      if (!id) return;

      // Inject Google Analytics script (gtag will be loaded by Google's script)
      const scriptId = `ga-script-${id}`;
      const existing = document.getElementById(scriptId);
      if (!existing) {
        const s = document.createElement('script');
        s.id = scriptId;
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
        document.head.appendChild(s);

        // Initialize dataLayer
        (window as any).dataLayer = (window as any).dataLayer || [];
        function gtag(..._args: any[]) {
          (window as any).dataLayer.push(arguments);
        }
        (window as any).gtag = gtag;
        gtag('js', new Date());
        // Send page_view immediately (no consent gate for basic page tracking)
        gtag('config', id, { 
          anonymize_ip: true,
          allow_ad_personalization_signals: false
        });
      }
    } catch (err) {
      // Don't block app if GA fails
      console.warn('GA initialization failed:', err);
    }
  }, []);

  return (
    <ThemeProvider>
      <PromoBanner />
      <CookieNotice />
      <SupportButton />
      <Router>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-200">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/squares" element={<SquaresPage />} />
            <Route path="/cubes" element={<CubesPage />} />
            <Route path="/fractions" element={<FractionsPage />} />
            <Route path="/advanced" element={<AdvancedPage />} />
            <Route path="/how-to-play" element={<AboutPage />} />
          </Routes>
        </div>
      </Router>
    </ThemeProvider>
  );
}